import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ParticipantGroup, Participant } from '../types';
import { XIcon } from './icons';
import Avatar from './Avatar';

interface AddGroupModalProps {
  onClose: () => void;
  onSaveGroup: (group: Omit<ParticipantGroup, 'id'> & { id?: string }) => void;
  initialGroupData?: ParticipantGroup | null;
  allParticipants: Participant[];
}

const AddGroupModal: React.FC<AddGroupModalProps> = ({ onClose, onSaveGroup, initialGroupData, allParticipants }) => {
  const isEditMode = Boolean(initialGroupData);
  const [name, setName] = useState('');
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (initialGroupData) {
      setName(initialGroupData.name);
      setSelectedParticipantIds(new Set(initialGroupData.participantIds));
    }
  }, [initialGroupData]);

  const handleToggleParticipant = (id: string) => {
    setSelectedParticipantIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const filteredParticipants = allParticipants.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isFormValid = name.trim() !== '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    onSaveGroup({
      id: initialGroupData && initialGroupData.id,
      name,
      participantIds: Array.from(selectedParticipantIds),
    });
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 p-4 overflow-y-auto flex items-start justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-app-border flex items-center justify-between">
            <h2 className="text-lg font-bold text-text-ink">{isEditMode ? 'Edit Group' : 'Create a New Group'}</h2>
            <button type="button" onClick={onClose} className="p-1 rounded-full text-text-muted hover:bg-neutral-100 active:bg-neutral-200">
              <XIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <label htmlFor="groupName" className="block text-xs font-medium text-text-muted mb-1">Group Name</label>
              <input
                type="text"
                id="groupName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="E.g., Power Users"
                className="w-full bg-white border border-app-border rounded-lg focus:outline-none focus:border-action focus:ring-1 focus:ring-action text-sm px-3 py-2 text-text-ink"
                required
              />
            </div>
            <div className="space-y-3">
              <label className="block text-xs font-medium text-text-muted">
                Select Members ({selectedParticipantIds.size} selected)
              </label>
              <input
                type="text"
                placeholder="Search participants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-app-border rounded-lg focus:outline-none focus:border-action focus:ring-1 focus:ring-action text-sm px-3 py-2 text-text-ink"
              />
              <div className="max-h-60 overflow-y-auto border border-app-border rounded-lg p-2 space-y-1">
                {filteredParticipants.map(participant => {
                  const isSelected = selectedParticipantIds.has(participant.id);
                  return (
                    <label
                      key={participant.id}
                      className={`flex items-center p-2 rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-action-soft hover:bg-action-mid' : 'hover:bg-app-bg'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleParticipant(participant.id)}
                        className="h-4 w-4 rounded border-app-border text-action focus:ring-action accent-action"
                      />
                      <Avatar
                        name={participant.name}
                        avatarUrl={participant.avatarUrl}
                        id={participant.id}
                        className="w-6 h-6 rounded-full object-cover ml-3"
                      />
                      <span className="ml-2 text-sm font-medium text-text-ink select-none">{participant.name}</span>
                    </label>
                  );
                })}
                {filteredParticipants.length === 0 && <p className="text-center text-sm text-text-muted py-4">No participants found.</p>}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-app-bg rounded-b-2xl flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="bg-white border border-app-border text-text-ink text-sm font-medium py-2 px-4 rounded-lg hover:bg-app-bg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid}
              className="bg-action text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-action/90 active:scale-95 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEditMode ? 'Save Changes' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddGroupModal;
